import { Map, OrderedSet } from 'immutable';

import { ClientActionType } from '../../../actions/client.actions';
import { Actions } from '../../../actions/index';
import { CellRunActions } from '../../../actions/notebooks/cell-run.actions';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { getTokenFromModeLink } from '@mode/shared/util-halogen';
import { NotebookVisualizationResource } from '@mode/legacy/data-access-model-js';
import { NotebookRunState } from '@mode/legacy/types-app-state';
import { NormalizedNotebookVizArray } from '@mode/legacy/types-app-state';

export const initialNormalizedNotebookViz = {
  runState: NotebookRunState.Uninitialized,
  tokens: OrderedSet<string>(),
  cellTokens: Map<string, string>(),
  cellRunTokens: Map<string, string>(),
  titles: Map<string, string>(),
  frozen: OrderedSet<string>(),
  resources: Map<string, NotebookVisualizationResource>(),
};

export function runStateReducer(
  state = initialNormalizedNotebookViz.runState,
  action: Actions
): NormalizedNotebookVizArray['runState'] {
  switch (action.type) {
    case CellActions.Type.StubLayoutVisuals:
      return NotebookRunState.Loading;
    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // falls through
    case CellActions.Type.LoadLayoutVisualsSuccess:
      return NotebookRunState.Succeeded;
    case CellActions.Type.LoadLayoutVisualsFailure:
      return NotebookRunState.Failed;
    case CellActions.Type.PolledLayoutVisuals:
      return action.reportRun.python_state === NotebookRunState.Skipped ? NotebookRunState.Skipped : state;
  }

  return state;
}

export function tokensReducer(
  state = initialNormalizedNotebookViz.tokens,
  action: Actions
): NormalizedNotebookVizArray['tokens'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case CellActions.Type.StubLayoutVisuals: // falls through
    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // falls through
    case CellActions.Type.LoadLayoutVisualsSuccess:
      return state.merge(action.vizResources.map((viz) => viz.token));
    case CellActions.Type.RemoveFromLayoutViaBuilder:
      return state.remove(action.vizToken);
    case CellActions.Type.RemoveFromLayout: {
      if (action.vizToken) {
        return state.remove(action.vizToken);
      }
      break;
    }
    case CellActions.Type.RemoveFromLayoutFailure:
      return state.add(action.vizToken);
    case CellActions.Type.AddToLayoutSuccess:
      return state.add(action.vizResource.token);
  }

  return state;
}

export function cellTokensReducer(
  state = initialNormalizedNotebookViz.cellTokens,
  action: Actions
): NormalizedNotebookVizArray['cellTokens'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case CellActions.Type.LoadVizCellSuccess:
      return state.set(action.vizToken, action.cellToken);
    case CellActions.Type.RemoveFromLayoutSuccess:
      return state.remove(action.vizToken);
    case CellActions.Type.AddToLayoutSuccess:
      return state.set(action.vizResource.token, action.cellToken);
  }

  return state;
}

export function cellRunTokensReducer(
  state = initialNormalizedNotebookViz.cellRunTokens,
  action: Actions
): NormalizedNotebookVizArray['cellRunTokens'] {
  switch (action.type) {
    case CellRunActions.Type.LoadLayoutCellRunSuccess:
      return state.set(action.cellToken, action.resource.token);

    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // for builder only
      return state.merge(
        action.vizResources.map<[string, string]>((res) => [
          getTokenFromModeLink(res.links('python_cell')),
          getTokenFromModeLink(res.links('python_cell_run')),
        ])
      );

    case CellRunActions.Type.LoadCellRunFromBatchSuccess: // for builder when running a cell
      return state.set(action.cellToken, action.resource.token);

    case CellActions.Type.AddToLayoutSuccess:
      return state.set(
        getTokenFromModeLink(action.vizResource.links('python_cell')),
        getTokenFromModeLink(action.vizResource.links('python_cell_run'))
      );
  }

  return state;
}

export function titlesReducer(
  state = initialNormalizedNotebookViz.titles,
  action: Actions
): NormalizedNotebookVizArray['titles'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case CellActions.Type.StubLayoutVisuals: // falls through
    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // falls through
    case CellActions.Type.LoadLayoutVisualsSuccess:
      return state.merge(action.vizResources.map<[string, string]>((viz) => [viz.token, viz.title]));
    case CellActions.Type.RemoveFromLayoutSuccess:
      return state.remove(action.vizToken);
    case CellActions.Type.AddToLayoutSuccess:
      return state.set(action.vizResource.token, action.vizResource.title);
    case CellActions.Type.UpdateVizTitle:
      return state.set(action.vizToken, action.title);
    case CellActions.Type.UpdateVizTitleFailure:
      if (action.vizResourcesArray.has(action.vizToken)) {
        return state.set(action.vizToken, action.vizResourcesArray.get(action.vizToken).title);
      }
      break;
  }

  return state;
}

export function frozenReducer(
  state = initialNormalizedNotebookViz.frozen,
  action: Actions
): NormalizedNotebookVizArray['frozen'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return state.clear();
    case CellActions.Type.StubLayoutVisuals: // falls through
    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // falls through
    case CellActions.Type.LoadLayoutVisualsSuccess:
      return state.merge(action.vizResources.filter((viz) => viz.frozen_in_layout).map((viz) => viz.token));
    case CellActions.Type.RemoveFromLayoutSuccess:
      return state.remove(action.vizToken);
  }

  return state;
}

export function resourcesReducer(
  state = initialNormalizedNotebookViz.resources,
  action: Actions
): NormalizedNotebookVizArray['resources'] {
  switch (action.type) {
    case ClientActionType.LoadReportSuccess:
      return state.clear();

    case CellActions.Type.StubLayoutVisuals: // falls through
    case CellActions.Type.LoadEmbeddedLayoutVisualsSuccess: // falls through
    case CellActions.Type.LoadLayoutVisualsSuccess:
      return state.merge(action.vizResources.map<[string, NotebookVisualizationResource]>((viz) => [viz.token, viz]));

    case CellActions.Type.UpdateVizSuccess: // falls through
    case CellActions.Type.AddToLayoutSuccess:
      return state.set(action.vizResource.token, action.vizResource);

    case CellActions.Type.RemoveFromLayoutSuccess:
      return state.remove(action.vizToken);
  }

  return state;
}

export const normalizedNotebookVizReducer = {
  runState: runStateReducer,
  tokens: tokensReducer,
  cellTokens: cellTokensReducer,
  cellRunTokens: cellRunTokensReducer,
  titles: titlesReducer,
  frozen: frozenReducer,
  resources: resourcesReducer,
};
